import { useState } from "react";
import api from "@/lib/api";
import { Archive, Undo2, CheckCircle2, DatabaseBackup } from "lucide-react";

// CC-224: held jobs (collection gate) carried to later weeks; release needs who confirmed + paid date.
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", dateStyle: "medium", timeStyle: "short" }) : "no AR file");
const pct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);

const ArStamp = ({ h, testId }) => (
  <div className="text-[10.5px] text-[color:var(--cc-ink-muted)]" data-testid={testId}
       title={h.ar_file_name || ""}>
    AR file uploaded {when(h.ar_uploaded_at)}{h.invoice_balance != null ? ` · balance then ${money(h.invoice_balance)}` : ""}
  </div>
);

const ReleaseForm = ({ h, onSubmit, onCancel, busy }) => {
  const [by, setBy] = useState("");
  const [on, setOn] = useState("");
  const [note, setNote] = useState("");
  return (
    <div className="flex flex-wrap items-end gap-2 mt-2" data-testid={`held-release-form-${h.job_number}`}>
      <label className="text-[11px]">Confirmed paid by
        <input className="block border border-[#d8dce3] rounded px-2 py-1 text-[12px] w-[170px]" value={by}
               onChange={(e) => setBy(e.target.value)} placeholder="e.g. Missie" data-testid={`held-release-by-${h.job_number}`} />
      </label>
      <label className="text-[11px]">Paid date in ServiceTitan
        <input type="date" className="block border border-[#d8dce3] rounded px-2 py-1 text-[12px]" value={on}
               onChange={(e) => setOn(e.target.value)} data-testid={`held-release-on-${h.job_number}`} />
      </label>
      <label className="text-[11px] flex-1 min-w-[160px]">Note (optional)
        <input className="block w-full border border-[#d8dce3] rounded px-2 py-1 text-[12px]" value={note}
               onChange={(e) => setNote(e.target.value)} data-testid={`held-release-note-${h.job_number}`} />
      </label>
      <button className="px-3 py-1.5 rounded bg-[#0e1420] text-white text-[12px] font-semibold disabled:opacity-50"
              disabled={busy || !by.trim() || !on} onClick={() => onSubmit({ confirmed_by: by.trim(), confirmed_on: on, note })}
              data-testid={`held-release-submit-${h.job_number}`}>Release to this week</button>
      <button className="px-2 py-1.5 text-[12px] text-[color:var(--cc-ink-muted)]" onClick={onCancel}>Cancel</button>
    </div>
  );
};

const PaidOutsideForm = ({ h, onSubmit, onCancel, busy }) => {
  const [on, setOn] = useState("");
  const [note, setNote] = useState("");
  return (
    <div className="flex flex-wrap items-end gap-2 mt-2" data-testid={`held-paidout-form-${h.job_number}`}>
      <label className="text-[11px]">Date paid
        <input type="date" className="block border border-[#d8dce3] rounded px-2 py-1 text-[12px]" value={on}
               onChange={(e) => setOn(e.target.value)} data-testid={`held-paidout-on-${h.job_number}`} />
      </label>
      <label className="text-[11px] flex-1 min-w-[220px]">Note (required)
        <input className="block w-full border border-[#d8dce3] rounded px-2 py-1 text-[12px]" value={note}
               placeholder="e.g. Paid by check 10/13, approved by Brett"
               onChange={(e) => setNote(e.target.value)} data-testid={`held-paidout-note-${h.job_number}`} />
      </label>
      <button className="px-3 py-1.5 rounded bg-[#8a5a1a] text-white text-[12px] font-semibold disabled:opacity-50"
              disabled={busy || !on || note.trim().length < 5} onClick={() => onSubmit({ paid_on: on, note: note.trim() })}
              data-testid={`held-paidout-submit-${h.job_number}`}>Mark paid outside the tool</button>
      <button className="px-2 py-1.5 text-[12px] text-[color:var(--cc-ink-muted)]" onClick={onCancel}>Cancel</button>
    </div>
  );
};

export const HeldJobsSection = ({ carryover, runId, slug, frozen, onChanged }) => {
  const [openForm, setOpenForm] = useState(null);
  const [paidForm, setPaidForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const co = carryover || {};
  const call = async (fn, okMsg) => {
    setBusy(true); setMsg(null);
    try { await fn(); setOpenForm(null); setMsg({ ok: true, text: okMsg }); await onChanged(); }
    catch (e) { const d = e?.response?.data?.detail; setMsg({ ok: false, text: typeof d === "string" ? d : JSON.stringify(d || e.message) }); }
    finally { setBusy(false); }
  };
  const release = (h, body) => call(() => api.post(`/payroll/runs/${runId}/held-jobs/${h.id}/release?tenant_slug=${slug}`, body), `Job ${h.job_number} released to this week.`);
  const undo = (ln) => call(() => api.delete(`/payroll/runs/${runId}/held-jobs/${ln.id}/release?tenant_slug=${slug}`), `Release of job ${ln.job_number} undone.`);
  const markPaidOutside = (h, body) => call(async () => {
    await api.post(`/payroll/runs/${runId}/held-jobs/${h.id}/paid-outside?tenant_slug=${slug}`, body);
    setPaidForm(null);
  }, `Job ${h.job_number} marked paid outside the tool — no commission will be added.`);
  const undoPaidOutside = (ln) => {
    const reason = window.prompt(`Undo "paid outside the tool" for job ${ln.job_number}? Reason (10+ characters):`);
    if (!reason) return;
    call(() => api.delete(`/payroll/runs/${runId}/held-jobs/${ln.id}/paid-outside?tenant_slug=${slug}`, { data: { reason } }),
         `Job ${ln.job_number} is held again.`);
  };
  const backfill = () => call(async () => {
    const { data } = await api.post(`/payroll/held-jobs/backfill?tenant_slug=${slug}`);
    const n = (data.runs || []).reduce((a, r) => a + (r.recorded_new || 0), 0);
    const skipped = (data.runs || []).filter((r) => r.skipped);
    setMsg({ ok: true, text: `Backfill done: ${n} new held job(s) recorded from approved runs.`
      + skipped.map((r) => ` Week ${r.pay_week_monday}: ${r.skipped}.`).join("") });
  }, "Backfill done.");

  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl" data-testid="held-jobs-section">
      <div className="px-5 py-3 border-b border-[#eef1f6] flex items-center gap-2">
        <Archive size={14} className="text-[#8a5a1a]" />
        <div className="font-semibold text-[14px]">Held jobs (collection gate)</div>
        <div className="ml-auto flex items-center gap-3">
          {co.carried_total_all_techs > 0 && (
            <span className="text-[12px] font-semibold text-[#14532d]" data-testid="held-carried-total">Carried pay this week: {money(co.carried_total_all_techs)}</span>
          )}
          <button className="text-[11.5px] inline-flex items-center gap-1 text-[#1c6a99] disabled:opacity-50" disabled={busy}
                  onClick={backfill} data-testid="held-backfill-btn"><DatabaseBackup size={12} /> Backfill held jobs</button>
        </div>
      </div>
      {msg && <div className={`px-5 py-2 text-[12px] ${msg.ok ? "text-[#14532d]" : "text-[#b42318]"}`} data-testid="held-jobs-msg">{msg.text}</div>}

      <Block title="Released to this week" empty="No held jobs released on this run." rows={co.carried_lines} testId="held-carried-lines"
        render={(ln) => (
          <div key={ln.id} className="flex items-start gap-3 py-2" data-testid={`held-carried-${ln.job_number}`}>
            <CheckCircle2 size={14} className="text-[#14532d] mt-0.5" />
            <div className="flex-1">
              <div className="text-[12.5px]"><b>{ln.tech_name}</b> · job {ln.job_number} · {money(ln.invoice_total)} · {ln.label}</div>
              <div className="text-[11px] text-[color:var(--cc-ink-muted)]">
                {pct(ln.rate)} ({ln.rate_reason}) → pays <b>{money(ln.paid_amount)}</b>{ln.flag ? ` · ${ln.flag}: not paid` : ""} · confirmed paid {ln.confirmed_on} by {ln.confirmed_by}
              </div>
              <ArStamp h={ln} testId={`held-carried-ar-${ln.job_number}`} />
            </div>
            {!frozen && <button className="text-[11.5px] inline-flex items-center gap-1 text-[#b42318]" disabled={busy}
                                onClick={() => undo(ln)} data-testid={`held-undo-${ln.job_number}`}><Undo2 size={12} /> Undo</button>}
          </div>
        )} />

      <Block title="Held from earlier weeks" empty="No open held jobs from earlier weeks." rows={co.open_held} testId="held-open-list"
        render={(h) => (
          <div key={h.id} className="py-2" data-testid={`held-open-${h.job_number}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="text-[12.5px]"><b>{h.tech_name}</b> · job {h.job_number} · {money(h.invoice_total)} · held week of {h.origin_pay_week_monday}</div>
                <div className="text-[11px] text-[color:var(--cc-ink-muted)]">{h.held_reason} · would pay {money(h.paid_amount)} at {pct(h.rate)}</div>
                <ArStamp h={h} testId={`held-open-ar-${h.job_number}`} />
              </div>
              {!frozen && openForm !== h.id && paidForm !== h.id && (
                <div className="flex gap-2">
                  <button className="px-2.5 py-1 rounded border border-[#0e1420] text-[11.5px] font-semibold" onClick={() => setOpenForm(h.id)}
                          data-testid={`held-release-btn-${h.job_number}`}>Release</button>
                  <button className="px-2.5 py-1 rounded border border-[#8a5a1a] text-[#8a5a1a] text-[11.5px] font-semibold" onClick={() => setPaidForm(h.id)}
                          data-testid={`held-paidout-btn-${h.job_number}`}>Mark paid outside the tool</button>
                </div>
              )}
            </div>
            {openForm === h.id && <ReleaseForm h={h} busy={busy} onCancel={() => setOpenForm(null)} onSubmit={(b) => release(h, b)} />}
            {paidForm === h.id && <PaidOutsideForm h={h} busy={busy} onCancel={() => setPaidForm(null)} onSubmit={(b) => markPaidOutside(h, b)} />}
          </div>
        )} />

      <Block title="Paid outside the tool (marked on this run · $0)" empty="None marked on this run." rows={co.paid_outside} testId="held-paidout-list"
        render={(ln) => (
          <div key={ln.id} className="flex items-start gap-3 py-2" data-testid={`held-paidout-${ln.job_number}`}>
            <div className="flex-1">
              <div className="text-[12.5px]"><b>{ln.tech_name}</b> · job {ln.job_number} · {money(ln.invoice_total)} · held week of {ln.origin_pay_week_monday} · <b>paid outside tool</b></div>
              <div className="text-[11px] text-[color:var(--cc-ink-muted)]">Paid {ln.paid_outside?.paid_on} · {ln.paid_outside?.note} · recorded by {ln.paid_outside?.recorded_by} · adds $0.00</div>
            </div>
            {!frozen && <button className="text-[11.5px] inline-flex items-center gap-1 text-[#b42318]" disabled={busy}
                                onClick={() => undoPaidOutside(ln)} data-testid={`held-paidout-undo-${ln.job_number}`}><Undo2 size={12} /> Undo</button>}
          </div>
        )} />

      <Block title="Held this week (recorded when this run is approved)" empty="No jobs held this week." rows={co.candidates} testId="held-this-week"
        render={(c) => (
          <div key={c.id} className="py-2" data-testid={`held-week-${c.job_number}`}>
            <div className="text-[12.5px]"><b>{c.tech_name}</b> · job {c.job_number} · {money(c.invoice_total)} · {c.held_reason}</div>
            <ArStamp h={c} testId={`held-week-ar-${c.job_number}`} />
          </div>
        )} />
      {(co.blocked || []).length > 0 && (
        <div className="px-5 py-2 text-[11.5px] text-[#b42318]" data-testid="held-blocked">
          {co.blocked.map((b) => `Job ${b.job_number} (${b.primary_tech}): ${b.detail}`).join(" · ")}
        </div>
      )}
    </div>
  );
};

const Block = ({ title, empty, rows, render, testId }) => (
  <div className="px-5 py-3 border-b border-[#eef1f6] last:border-b-0" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1">{title}</div>
    {(rows || []).length ? <div className="divide-y divide-[#f1f3f7]">{rows.map(render)}</div>
      : <div className="text-[12px] text-[color:var(--cc-ink-muted)]">{empty}</div>}
  </div>
);
